import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OTP_LENGTH } from '@/features/auth/components/OtpInput';
import {
  useOnboardingFieldsQuery,
  useResendForgotOtpMutation,
  useResendSignupOtpMutation,
  useVerifyEmailOtpMutation,
  useVerifyForgotOtpMutation,
  useVerifyPhoneSignupMutation,
} from '@/features/auth/api/useAuthMutations';
import { getMobileConfig } from '@/features/auth/utils/mobileNumber';
import {
  getSignupSteps,
  hasSignupProfileStep,
} from '@/features/auth/utils/signupSteps';
import { routes } from '@/shared/config/routes';

export type OtpFlowType = 'Email' | 'Phone' | 'Forgot';

type OtpLocationState = {
  type?: OtpFlowType;
  email?: string;
  mobileNumber?: string;
};

function resolveOtpType(state: OtpLocationState): OtpFlowType {
  if (state.type === 'Forgot') return 'Forgot';
  if (state.type === 'Phone' || localStorage.getItem('otp_type') === 'Phone') {
    return 'Phone';
  }
  return 'Email';
}

export function useOtpVerifyPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as OtpLocationState | null) ?? {};
  const type = resolveOtpType(state);

  const email = state.email || localStorage.getItem('user_email') || '';
  const mobileNumber =
    state.mobileNumber || localStorage.getItem('user_mobile') || '';
  const forgotIdentifier =
    state.email || localStorage.getItem('user_mobile') || '';

  const [otp, setOtp] = useState(() => new Array(OTP_LENGTH).fill(''));

  const role = localStorage.getItem('role');
  const { data: fieldsData } = useOnboardingFieldsQuery(
    type === 'Forgot' ? null : role,
  );
  const steps = getSignupSteps(hasSignupProfileStep(fieldsData));
  const verifyEmail = useVerifyEmailOtpMutation();
  const verifyPhone = useVerifyPhoneSignupMutation();
  const verifyForgot = useVerifyForgotOtpMutation();
  const resendOtp = useResendSignupOtpMutation();
  const resendForgot = useResendForgotOtpMutation();

  useEffect(() => {
    setOtp(new Array(OTP_LENGTH).fill(''));
  }, [type]);

  useEffect(() => {
    if (type === 'Forgot') return;
    const handlePopState = () => {
      navigate(routes.signup, { replace: true });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate, type]);

  const mobileConfig = getMobileConfig();
  const destination =
    type === 'Phone'
      ? mobileNumber
        ? `${mobileConfig.code}-${mobileNumber}`
        : 'your phone'
      : email || forgotIdentifier || 'your email';

  const isPending =
    verifyEmail.isPending || verifyPhone.isPending || verifyForgot.isPending;

  const title =
    type === 'Forgot'
      ? 'Reset verification'
      : type === 'Phone'
        ? 'Phone verification'
        : 'Email verification';

  const clearOtp = () => setOtp(new Array(OTP_LENGTH).fill(''));

  const handleSubmit = () => {
    const otpString = otp.join('');
    if (otpString.length !== OTP_LENGTH) return;

    if (type === 'Forgot') {
      verifyForgot.mutate(
        { identifier: forgotIdentifier, otp: otpString },
        { onSuccess: clearOtp },
      );
      return;
    }

    const sessionId = localStorage.getItem('SESSION_ID_TWO') || '';

    if (type === 'Phone') {
      verifyPhone.mutate(
        { sessionId, mobileNumber, phoneOtp: otpString },
        {
          onSuccess: () => {
            clearOtp();
            navigate(
              hasSignupProfileStep(fieldsData)
                ? routes.userDetail
                : routes.createPassword,
            );
          },
        },
      );
      return;
    }

    verifyEmail.mutate(
      { sessionId, emailOtp: otpString },
      { onSuccess: clearOtp },
    );
  };

  const handleResend = () => {
    if (type === 'Forgot') {
      resendForgot.mutate(
        { identifier: forgotIdentifier },
        { onSuccess: clearOtp },
      );
      return;
    }

    resendOtp.mutate(
      {
        sessionId: localStorage.getItem('SESSION_ID_TWO') || '',
        type: type === 'Phone' ? 'SIGNUP_PHONE' : 'SIGNUP_EMAIL',
      },
      { onSuccess: clearOtp },
    );
  };

  const handleBack = () => {
    navigate(type === 'Forgot' ? routes.forgotPassword : routes.signup);
  };

  return {
    type,
    otp,
    setOtp,
    steps,
    title,
    destination,
    isPending,
    handleSubmit,
    handleResend,
    handleBack,
  };
}
